import React, { useMemo, useState } from 'react';
import { Modal, Select, Radio } from 'antd';
import ResultTable from '../../components/table/ResultTable';
import ResultHeader from '../../components/table/ResultHeader';
import ResultBody from '../../components/table/ResultBody';
import ResultRow from '../../components/table/ResultRow';
import ResultHeaderCell from '../../components/table/ResultHeaderCell';
import ResultBodyCell from '../../components/table/ResultBodyCell';
import HecRasHelper from '../../helpers/HecRasHelper';

/**
 * Render score with color
 */
const renderScore = (score) => {
    return (
        <span style={{ color: HecRasHelper.getScoreColor(score), fontWeight: 'bold' }}>
            {score.toFixed(2)}
        </span>
    );
};

// Summary tablosundaki kategori siralamasi ve etiketleri.
const CATEGORY_ORDER = [
    { key: 'crossSection', label: 'Cross Section' },
    { key: 'bridge', label: 'Bridge' },
    { key: 'culvert', label: 'Culvert' },
    { key: 'overall', label: 'Overall Flow and Geometry' },
];

// Mesaj kodu -> aciklama. Tum kodlar buildMessageCountRows'un dondurdugu ALLOWED
// listelerle birebir ortusur (Cross Section 18, Bridge 9, Culvert 11, Overall 2).
const CODE_DESCRIPTIONS = {
    // Cross Section
    'XS 05': 'Need for additional downstream cross sections.',
    'XS 06': 'Inconsistent left and right bank station elevations.',
    'FW 02': 'Encroachment station placement issues.',
    'FL 05': 'Flow rate mismatch between upstream and downstream bridge cross sections.',
    'OP 01': 'Flow is not contained within the cross section.',
    'RT 01': 'Non-standard contraction/expansion coefficients at natural cross sections.',
    'RT 02': 'Non-standard contraction/expansion coefficients at structure cross sections.',
    'RT 05': "Missing horizontally varied Manning's n value at the bank station.",
    'XY 01': 'Missing cross section georeferencing.',
    'XY 02': 'Bank stations do not enclose the stream centerline.',
    'XY 03': 'Cutline length does not match the cross section length.',
    'XY 05': 'Excessive skew angle between the cutline and the stream centerline.',
    'XY 07': 'Cross section cutline does not intersect the stream centerline.',
    'XY 10': 'Cross section cutlines intersect with each other.',
    'XY 20': 'Cross section cutline intersects the stream centerline at multiple locations.',
    'XY 15': 'Discrepancy in defined vs. estimated Overbank downstream reach lengths.',
    'XY 11': 'Discrepancy in defined vs. calculated Channel downstream reach lengths.',
    'XY 19': 'Sharp turns detected in cross section cutline segments.',
    // Bridge
    'XY 06': 'Bridge skew angle exceeds threshold.',
    'ST 01': 'Contraction/expansion ratios deviate from user preferences.',
    'ST 02': 'Bridges do not follow recommended modeling methods.',
    'ST 03': 'Maximum submergence ratio differs from the default value (0.98).',
    'ST 05': 'Missing bridge section.',
    'ST 06': 'Inconsistent ineffective flow areas at upstream and downstream cross sections.',
    'ST 12': 'Inconsistent upstream and downstream abutment slopes.',
    'ST 15': 'Inconsistent upstream and downstream bridge opening widths.',
    'ST 10': 'Bounding cross sections are outside the recommended distance from the structure face.',
    // Culvert
    'RT 06': 'Culvert entrance/exit loss coefficient outside recommended limits.',
    'RT 07': "Culvert Manning's n value outside recommended limits.",
    'RT 08': 'Weir flow coefficient outside recommended limits.',
    'ST 04': 'Maximum submergence ratio differs from the default value (0.98).',
    'ST 07': 'Culvert geometry does not fit between the bounding cross sections.',
    'ST 09': 'Culvert centerline station is not located within the channel.',
    'ST 13': 'Upstream invert elevation is lower than downstream invert (adverse slope).',
    'ST 14': 'Culvert slope exceeds the defined maximum threshold.',
    // Overall Flow and Geometry
    'FW 05': 'Flow rate mismatch between 100-year and floodway profiles.',
    'XY 16': 'Reach invert line is not drawn upstream to downstream.',
};

// Summary: TEK tabloda, her kategori basligi colSpan=3 bir satir olarak, kod
// satirlari altinda. Scroll icine alinir (modal uzamasin). Description sarmalanir.
function SummaryView({ history }) {
    const countRows = useMemo(() => HecRasHelper.buildMessageCountRows(history), [history]);

    return (
        <ResultTable
            noDescription
            scrollable={{ height: 800 }}
            style={{ tableLayout: 'fixed' }}
            maxWidth={'100%'}
        >
            <ResultHeader>
                <ResultRow>
                    <ResultHeaderCell scrollable={{ top: 0 }} style={{ textAlign: 'left', width: HecRasHelper.getWidth(10) }}>ID</ResultHeaderCell>
                    <ResultHeaderCell scrollable={{ top: 0 }} style={{ textAlign: 'left', width: HecRasHelper.getWidth(100)  }}>Description</ResultHeaderCell>
                    <ResultHeaderCell scrollable={{ top: 0 }} style={{ textAlign: 'right', width: HecRasHelper.getWidth(8) }}>Count</ResultHeaderCell>
                </ResultRow>
            </ResultHeader>
            <ResultBody>
                {CATEGORY_ORDER.map(cat => {
                    const rows = countRows?.[cat.key] || [];
                    if (rows.length === 0) return null;
                    return (
                        <React.Fragment key={cat.key}>
                            <ResultRow>
                                <ResultHeaderCell colSpan={3} style={{ textAlign: 'center', fontWeight: 'bold' }}>
                                    {cat.label}
                                </ResultHeaderCell>
                            </ResultRow>
                            {rows.map(r => (
                                <ResultRow key={`${cat.key}-${r.messageCode}`}>
                                    <ResultBodyCell style={{ textAlign: 'left' }}>{r.messageCode}</ResultBodyCell>
                                    <ResultBodyCell style={{ textAlign: 'left', whiteSpace: 'normal', wordBreak: 'break-word' }}>{CODE_DESCRIPTIONS[r.messageCode] || ''}</ResultBodyCell>
                                    <ResultBodyCell style={{ textAlign: 'right' }}>{r.count}</ResultBodyCell>
                                </ResultRow>
                            ))}
                        </React.Fragment>
                    );
                })}
            </ResultBody>
        </ResultTable>
    );
}

export default function ScoreAnalysisModal({ visible, onClose, history }) {
    const [selectedMessageCodes, setSelectedMessageCodes] = useState([]);
    // 'summary' | 'crossSection' | 'bridge' | 'culvert' | 'overall'
    const [selectedCategory, setSelectedCategory] = useState('summary');

    const analysisData = useMemo(() => {
        if (!history) return null;

        const rows = HecRasHelper.buildItemScoreRows(history);

        const sortByRS = (a, b) => parseFloat(b.riverStation) - parseFloat(a.riverStation);
        const crossSectionData = [...rows.crossSection].sort(sortByRS);
        const bridgeData = [...rows.bridge].sort(sortByRS);
        const culvertData = [...rows.culvert].sort(sortByRS);
        const overallData = rows.overall;

        return {
            crossSectionData,
            bridgeData,
            culvertData,
            overallData,
        };
    }, [history]);

    // Get current data based on selected category
    const currentData = useMemo(() => {
        if (!analysisData) return [];
        if (selectedCategory === 'bridge') return analysisData.bridgeData;
        if (selectedCategory === 'culvert') return analysisData.culvertData;
        if (selectedCategory === 'overall') return analysisData.overallData;
        if (selectedCategory === 'crossSection') return analysisData.crossSectionData;
        return [];
    }, [analysisData, selectedCategory]);

    // Get unique message codes from current data
    const messageCodeOptions = useMemo(() => {
        if (!currentData || currentData.length === 0) return [];
        const codes = new Set();
        currentData.forEach(item => {
            item.messages?.split(', ').forEach(code => {
                if (code.trim()) codes.add(code.trim());
            });
        });
        return Array.from(codes).sort().map(code => ({ label: code, value: code }));
    }, [currentData]);

    // Filter data based on selections
    const filteredData = useMemo(() => {
        if (!currentData || currentData.length === 0) return [];

        return currentData.filter(item => {
            if (selectedMessageCodes.length > 0) {
                const itemCodes = item.messages?.split(', ').map(c => c.trim()) || [];
                if (!selectedMessageCodes.some(code => itemCodes.includes(code))) {
                    return false;
                }
            }
            return true;
        });
    }, [currentData, selectedMessageCodes]);

    // Score/kategori tablosu sutun genislikleri. RS ve Score SABIT (getWidth) ve tum
    // sekmelerde AYNI. descFillWidth ile tablo table-layout:fixed + width:100% olur;
    // fazlaligi DOLDURAN auto sutun (non-overall: enjekte Description; overall: manuel
    // Description) emdigi icin RS/Score sabit genislikte kalir, sekmeler arasi kaymaz.
    const RS_W = 12;      // River Station (tum sekmelerde ayni)
    const SCORE_W = 8;    // Score
    const MSG_W = 50;     // Messages (yalnizca non-overall; sabit)

    if (!history) {
        return (
            <Modal
                title="Score Analysis"
                open={visible}
                onCancel={onClose}
                footer={null}
                width={1100}
                styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}
            >
                <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                    No history data available. Please run a check first.
                </div>
            </Modal>
        );
    }

    return (
        <Modal
            title="Score Analysis"
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1000}
            styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}
        >
            {(() => {
                const totalScore = HecRasHelper.calculateOverallScore(history);
                const totalScoreColor = HecRasHelper.getScoreColor(totalScore);
                return (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 16px',
                            marginBottom: 12,
                            background: '#fafafa',
                            border: '1px solid #f0f0f0',
                            borderRadius: 6,
                        }}
                    >
                        <span style={{ fontSize: 13, color: '#8c8c8c', fontWeight: 500 }}>Total Score</span>
                        <span style={{ fontSize: 22, fontWeight: 600, color: totalScoreColor, lineHeight: 1.2 }}>
                            {totalScore !== null && totalScore !== undefined ? totalScore.toFixed(2) : '-'}
                        </span>
                    </div>
                );
            })()}

            <div style={{ marginBottom: 12 }}>
                <Radio.Group
                    value={selectedCategory}
                    onChange={(e) => {
                        setSelectedCategory(e.target.value);
                        setSelectedMessageCodes([]); // Clear message code filter when category changes
                    }}
                    buttonStyle="solid"
                >
                    <Radio.Button value="summary">Summary</Radio.Button>
                    <Radio.Button value="crossSection">Cross Section</Radio.Button>
                    <Radio.Button value="bridge">Bridge</Radio.Button>
                    <Radio.Button value="culvert">Culvert</Radio.Button>
                    <Radio.Button value="overall">Overall Flow and Geometry</Radio.Button>
                </Radio.Group>
            </div>

            {selectedCategory === 'summary' ? (
                <SummaryView history={history} />
            ) : (
                <>
                    {selectedCategory !== 'overall' && (
                        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                            <Select
                                size='small'
                                mode="multiple"
                                allowClear
                                placeholder="Filter by Message Code..."
                                value={selectedMessageCodes}
                                onChange={setSelectedMessageCodes}
                                options={messageCodeOptions}
                                style={{ flex: 1 }}
                                maxTagCount={3}
                                showSearch
                                filterOption={(input, option) =>
                                    option?.label?.toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </div>
                    )}

                    {(() => {
                        const total = currentData?.length || 0;
                        const hasItems = total > 0;
                        const avg = hasItems
                            ? currentData.reduce((acc, r) => acc + r.score, 0) / total
                            : null;
                        const categoryLabel = selectedCategory === 'bridge' ? 'Bridge'
                            : selectedCategory === 'culvert' ? 'Culvert'
                            : selectedCategory === 'overall' ? 'Overall'
                            : 'Cross Section';
                        const uniqueCodes = new Set();
                        (currentData || []).forEach(item => {
                            item.messages?.split(', ').forEach(code => {
                                const c = code.trim();
                                if (c) uniqueCodes.add(c);
                            });
                        });
                        const avgColor = HecRasHelper.getScoreColor(avg);
                        const cardStyle = {
                            flex: 1,
                            padding: '12px 16px',
                            background: '#fff',
                            border: '1px solid #f0f0f0',
                            borderRadius: 6,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                        };
                        const labelStyle = { fontSize: 12, color: '#8c8c8c', marginBottom: 4 };
                        const valueStyle = { fontSize: 22, fontWeight: 600, lineHeight: 1.2 };
                        return (
                            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                                <div style={cardStyle}>
                                    <div style={labelStyle}>Total {categoryLabel}</div>
                                    <div style={{ ...valueStyle, color: '#262626' }}>{total}</div>
                                </div>
                                <div style={cardStyle}>
                                    <div style={labelStyle}>Unique Message IDs</div>
                                    <div style={{ ...valueStyle, color: '#262626' }}>{uniqueCodes.size}</div>
                                </div>
                                <div style={cardStyle}>
                                    <div style={labelStyle}>Avg Score</div>
                                    <div style={{ ...valueStyle, color: avgColor }}>{avg !== null ? avg.toFixed(2) : '-'}</div>
                                </div>
                            </div>
                        );
                    })()}

                    {(!currentData || currentData.length === 0) ? (
                        <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>No data available for this category</div>
                    ) : (
                        <ResultTable scrollable={{ height: 400 }} descFillWidth noDescription={selectedCategory === 'overall'}>
                            <ResultHeader>
                                <ResultRow>
                                    <ResultHeaderCell scrollable={{ top: 0 }} style={{ textAlign: 'left', verticalAlign: 'top', width: HecRasHelper.getWidth(RS_W) }}>River Station</ResultHeaderCell>
                                    <ResultHeaderCell scrollable={{ top: 0 }} style={{ textAlign: 'right', verticalAlign: 'top', width: HecRasHelper.getWidth(SCORE_W) }}>Score</ResultHeaderCell>
                                    {selectedCategory !== 'overall' && (
                                        <ResultHeaderCell scrollable={{ top: 0 }} style={{ textAlign: 'left', verticalAlign: 'top', width: HecRasHelper.getWidth(MSG_W) }}>Messages</ResultHeaderCell>
                                    )}
                                    {selectedCategory === 'overall' && (
                                        <ResultHeaderCell scrollable={{ top: 0 }} style={{ textAlign: 'left', verticalAlign: 'top' }}>Description</ResultHeaderCell>
                                    )}
                                </ResultRow>
                            </ResultHeader>
                            <ResultBody>
                                {filteredData.map((row, index) => (
                                    <ResultRow key={`${row.type}-${row.riverStation}-${index}`}>
                                        <ResultBodyCell style={{ textAlign: 'left', verticalAlign: 'top', width: HecRasHelper.getWidth(RS_W) }}>{row.riverStation}</ResultBodyCell>
                                        <ResultBodyCell style={{ textAlign: 'right', verticalAlign: 'top', width: HecRasHelper.getWidth(SCORE_W) }}>{renderScore(row.score)}</ResultBodyCell>
                                        {selectedCategory !== 'overall' && (
                                            <ResultBodyCell style={{ textAlign: 'left', verticalAlign: 'top', whiteSpace: 'normal', wordBreak: 'break-word', width: HecRasHelper.getWidth(MSG_W) }}>{row.messages}</ResultBodyCell>
                                        )}
                                        {selectedCategory === 'overall' && (
                                            <ResultBodyCell style={{ textAlign: 'left', verticalAlign: 'top', whiteSpace: 'normal', wordBreak: 'break-word' }}>-</ResultBodyCell>
                                        )}
                                    </ResultRow>
                                ))}
                            </ResultBody>
                        </ResultTable>
                    )}
                </>
            )}
        </Modal>
    );
}
